<template>
  <!-- Modern Glassmorphism Header -->
  <div :class="{ 'dark-mode': isDarkMode }">
    <v-app-bar app class="glass-header" :class="{ 'dark-header': isDarkMode }" elevation="0" height="70">
      <v-container class="d-flex align-center py-0">
        <img src="@/assets/img/logo.png" height="36" class="logo-img" @click="$router.push('/client-dashboard')" style="cursor: pointer;" />

        <!-- Desktop Navigation -->
        <div class="nav-links d-none d-md-flex align-center ml-auto">
          <v-btn text class="text-none nav-link" :class="{ 'active-nav': isActive('/client-dashboard'), 'dark-nav': isDarkMode }" @click="$router.push('/client-dashboard')">
            <v-icon small left class="material-icons-round">dashboard</v-icon>
            Dashboard
          </v-btn>
          <v-btn text class="text-none nav-link" :class="{ 'active-nav': isActive('/edit-card'), 'dark-nav': isDarkMode }" @click="$router.push('/edit-card')">
            <v-icon small left class="material-icons-round">badge</v-icon>
            V-Card
          </v-btn>
          <v-btn text class="text-none nav-link" :class="{ 'active-nav': isActive('/my-referrals'), 'dark-nav': isDarkMode }" @click="$router.push('/my-referrals')">
            <v-icon small left class="material-icons-round">people</v-icon>
            Referrals
          </v-btn>
          <v-btn text class="text-none nav-link" :class="{ 'active-nav': isActive('/referring-agent-dealerships'), 'dark-nav': isDarkMode }" @click="$router.push('/referring-agent-dealerships')">
            <v-icon small left class="material-icons-round">store</v-icon>
            Dealerships
          </v-btn>
          <!-- Dark/Light Mode Toggle -->
          <v-btn icon small class="theme-toggle-btn ml-2" @click="toggleDarkMode" :title="isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'">
            <v-icon class="material-icons-round" :color="isDarkMode ? 'amber' : 'grey darken-1'">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</v-icon>
          </v-btn>
          <v-menu offset-y nudge-bottom="10" transition="slide-y-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-avatar size="38" class="profile-nav-avatar ml-4" v-bind="attrs" v-on="on">
                <img v-if="avatar" :src="avatar" alt="Profile" />
                <img v-else src="@/assets/img/avatar.png" alt="Profile" />
              </v-avatar>
            </template>
            <v-card class="profile-dropdown" width="220">
              <v-list dense class="py-2">
                <v-list-item class="px-4 py-2 mb-2" style="border-bottom: 1px solid #f0f0f0;">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">{{ userName }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">Referring Agent</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item @click="$router.push('/edit-card')" class="menu-item">
                  <v-icon small class="material-icons-round mr-3" color="grey darken-1">settings</v-icon>
                  <v-list-item-title>Edit Profile</v-list-item-title>
                </v-list-item>
                <v-list-item @click="logout" class="menu-item">
                  <v-icon small class="material-icons-round mr-3" color="red lighten-1">logout</v-icon>
                  <v-list-item-title class="red--text text--lighten-1">Logout</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
          <v-btn v-if="isFromAdmin" color="#6366F1" dark small class="text-none ml-4 back-admin-btn" @click="backToAdmin">
            <v-icon small left class="material-icons-round">arrow_back</v-icon>
            Back to Admin
          </v-btn>
        </div>

        <!-- Mobile Menu -->
        <v-spacer class="d-md-none"></v-spacer>
        <!-- Mobile Dark Mode Toggle -->
        <v-btn icon small class="d-md-none theme-toggle-btn mr-1" @click="toggleDarkMode">
          <v-icon class="material-icons-round" :color="isDarkMode ? 'amber' : 'grey darken-1'">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</v-icon>
        </v-btn>
        <v-btn icon class="d-md-none mobile-menu-btn" :class="{ 'dark-menu-btn': isDarkMode }" @click="mobileMenu = true">
          <v-icon class="material-icons-round">menu</v-icon>
        </v-btn>
      </v-container>
    </v-app-bar>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer v-model="mobileMenu" temporary right app class="mobile-drawer">
      <div class="drawer-header pa-4">
        <v-avatar size="60" class="mb-3">
          <img v-if="avatar" :src="avatar" alt="Profile" />
          <img v-else src="@/assets/img/avatar.png" alt="Profile" />
        </v-avatar>
        <h4 class="mb-0">{{ userName }}</h4>
        <span class="text-caption" style="color: rgba(255,255,255,0.7);">Referring Agent</span>
      </div>
      <v-divider></v-divider>
      <v-list nav dense class="pt-3">
        <v-list-item @click="$router.push('/client-dashboard'); mobileMenu = false" class="mobile-nav-item">
          <v-icon class="material-icons-round mr-3" color="indigo">dashboard</v-icon>
          <v-list-item-title>Dashboard</v-list-item-title>
        </v-list-item>
        <v-list-item @click="$router.push('/edit-card'); mobileMenu = false" class="mobile-nav-item">
          <v-icon class="material-icons-round mr-3" color="indigo">badge</v-icon>
          <v-list-item-title>V-Card</v-list-item-title>
        </v-list-item>
        <v-list-item @click="$router.push('/my-referrals'); mobileMenu = false" class="mobile-nav-item">
          <v-icon class="material-icons-round mr-3" color="indigo">people</v-icon>
          <v-list-item-title>My Referrals</v-list-item-title>
        </v-list-item>
        <v-list-item @click="$router.push('/referring-agent-dealerships'); mobileMenu = false" class="mobile-nav-item">
          <v-icon class="material-icons-round mr-3" color="indigo">store</v-icon>
          <v-list-item-title>Dealerships</v-list-item-title>
        </v-list-item>
        <v-list-item @click="$router.push('/edit-card'); mobileMenu = false" class="mobile-nav-item">
          <v-icon class="material-icons-round mr-3" color="indigo">settings</v-icon>
          <v-list-item-title>Edit Profile</v-list-item-title>
        </v-list-item>
        <v-divider class="my-3"></v-divider>
        <v-list-item @click="logout" class="mobile-nav-item">
          <v-icon class="material-icons-round mr-3" color="red lighten-1">logout</v-icon>
          <v-list-item-title class="red--text text--lighten-1">Logout</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="isFromAdmin" @click="backToAdmin" class="mobile-nav-item">
          <v-icon class="material-icons-round mr-3" color="indigo">arrow_back</v-icon>
          <v-list-item-title class="indigo--text">Back to Admin</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </div>
</template>

<script>
import Vue from 'vue';

export default {
  name: 'ReferringAgentHeader',
  data() {
    return {
      mobileMenu: false,
      avatar: null,
      userName: '',
      isFromAdmin: false,
      isDarkMode: true,
      wasabi_url: process.env.VUE_APP_WASBI_URL,
    };
  },
  created() {
    this.loadUserData();
    this.checkFromAdmin();
    this.loadDarkModePreference();
  },
  mounted() {
    this.checkFromAdmin();
    window.addEventListener('storage', this.handleStorageChange);
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.handleStorageChange);
  },
  methods: {
    isActive(path) {
      return this.$route.path === path;
    },
    loadUserData() {
      const firstName = localStorage.getItem('first_name') || '';
      const lastName = localStorage.getItem('last_name') || '';
      this.userName = `${firstName} ${lastName}`.trim() || localStorage.getItem('name') || 'User';
      this.avatar = localStorage.getItem('avatar') || null;
    },
    checkFromAdmin() {
      this.isFromAdmin = localStorage.getItem('admin_token') || localStorage.getItem('from_admin') == '1' ? true : false;
    },
    loadDarkModePreference() {
      const saved = localStorage.getItem('referring_agent_dark_mode');
      this.isDarkMode = saved !== null ? saved === 'true' : true;
    },
    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem('referring_agent_dark_mode', this.isDarkMode);
      this.$emit('dark-mode-changed', this.isDarkMode);
      window.dispatchEvent(new CustomEvent('referringAgentDarkModeChanged', { detail: { isDarkMode: this.isDarkMode } }));
    },
    handleStorageChange(event) {
      if (event.key === 'referring_agent_dark_mode') {
        this.isDarkMode = event.newValue === 'true';
      }
    },
    logout() {
      localStorage.clear();
      this.$router.push('/login?portal=referring');
    },
    backToAdmin() {
      const adminToken = localStorage.getItem('admin_token');
      const fromAdmin = localStorage.getItem('from_admin');
      const fromAdminEmail = localStorage.getItem('from_admin_email');

      if (adminToken) {
        // Use admin_token method
        axios.post('/api/switch-back-to-admin', {}, {
          headers: { Authorization: 'Bearer ' + adminToken }
        }).then(res => {
          const data = res.data;
          if (data.flag === 1) {
            localStorage.setItem('token', adminToken);
            localStorage.removeItem('admin_token');
            this.$router.push('/admin/dashboard');
          }
        }).catch(() => {
          this.$toast.error('Failed to switch back to admin', { position: 'top-right' });
        });
      } else if (fromAdmin == '1' && fromAdminEmail) {
        // Use from_admin method - login back as admin using stored email
        axios.post('/api/auth/login-admin-client-admin', {
          email: fromAdminEmail
        }).then(res => {
          const data = res.data;
          if (data.flag === 1) {
            const result = data.data;
            localStorage.setItem('token', data.token.token);
            localStorage.setItem('user_id', result.user_id);
            localStorage.setItem('email', result.email);
            localStorage.setItem('first_name', result.first_name);
            localStorage.setItem('name', result.first_name + ' ' + result.last_name);
            localStorage.setItem('is_admin', 1);
            localStorage.removeItem('from_admin');
            localStorage.removeItem('from_admin_email');
            if (result.roles && result.roles.length > 0) {
              localStorage.setItem('role', JSON.stringify(result.roles[0]));
            }
            this.$router.push('/admin/dashboard');
            location.reload();
          } else {
            this.$toast.error(data.message || 'Failed to switch back to admin', { position: 'top-right' });
          }
        }).catch(() => {
          this.$toast.error('Failed to switch back to admin', { position: 'top-right' });
        });
      }
    }
  }
};
</script>

<style scoped>
/* Glass Header */
.glass-header {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
}

/* Dark Mode Header */
.glass-header.dark-header {
  background: rgba(26, 26, 46, 0.95) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo-img {
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
}

/* Navigation */
.nav-links { gap: 8px; }

.nav-link {
  font-size: 14px !important;
  font-weight: 500;
  color: #64748b !important;
  border-radius: 10px;
  padding: 8px 16px !important;
  transition: all 0.2s ease;
}

.nav-link:hover { color: #6366F1 !important; background: rgba(99, 102, 241, 0.08); }
.nav-link.dark-nav { color: rgba(255, 255, 255, 0.7) !important; }
.nav-link.dark-nav:hover { color: #a5b4fc !important; background: rgba(99, 102, 241, 0.15); }
.active-nav { color: #6366F1 !important; background: rgba(99, 102, 241, 0.1); }
.active-nav.dark-nav { color: #a5b4fc !important; background: rgba(99, 102, 241, 0.2); }

.profile-nav-avatar {
  cursor: pointer;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}
.profile-nav-avatar:hover { border-color: #6366F1; transform: scale(1.05); }

.profile-dropdown {
  border-radius: 16px !important;
  box-shadow: 0 20px 40px rgba(0,0,0,0.12) !important;
  border: 1px solid #f0f0f0;
}

.menu-item { border-radius: 8px; margin: 0 8px; transition: background 0.2s ease; }
.menu-item:hover { background: #f8fafc; }

.back-admin-btn { border-radius: 10px; text-transform: none; font-weight: 600; }

.mobile-menu-btn { color: #1e293b; }
.mobile-menu-btn.dark-menu-btn { color: rgba(255, 255, 255, 0.9); }

.theme-toggle-btn { transition: all 0.3s ease; }
.theme-toggle-btn:hover { transform: rotate(20deg); }

/* Mobile Drawer */
.mobile-drawer { width: 280px !important; }
.drawer-header {
  background: linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%);
  color: white;
  text-align: center;
}
.mobile-nav-item { border-radius: 12px; margin: 4px 12px; }
</style>

